import { request } from '@/shared/utils/requestBuilder';

import type {
  ChaveDoCliente,
  ChaveEmitida,
  DadosDaChaveNova,
  ErroDasChaves,
  RotacaoDeChave,
} from '../types/chavesDeApiTypes';

/**
 * Rotas de chaves do escritório (plano 18, fatias 1 e 4), espelho de `chavesDoCliente.routes.ts` e
 * `chavesDoCliente.validators.ts` do core.
 */
const rotaDasChaves = (escritorioId: string) =>
  `/escritorios/${encodeURIComponent(escritorioId)}/chaves-de-api`;
const rotaDaChave = (escritorioId: string, chaveId: string) =>
  `${rotaDasChaves(escritorioId)}/${encodeURIComponent(chaveId)}`;

/**
 * Decisão (e) de 01/10: estas rotas exigem `X-Workspace-Id` igual ao `:id` (400 sem ele), e o 403
 * delas é a recusa de permissão de quem administra chaves. A recuperação de workspace do
 * `requestBuilder` refaria a chamada sem o header e entregaria o 400 no lugar do 403 — por isso
 * todas as chamadas daqui saem com ela desligada.
 */
const chamarRotaDasChaves = (endpoint: string, method: 'GET' | 'POST', body?: object) =>
  request({ endpoint, method, body, recuperarWorkspaceNo403: false });

/** Sucesso de criar e rotacionar (R4.2): o DTO da listagem com o texto da chave, uma vez. */
interface ChaveEmitidaDaApi extends ChaveDoCliente {
  textoDaChave: string;
}

/**
 * A mensagem do core nesta rota é de diagnóstico (traz o id da chave e o estado em enum), não de
 * tela. Por isso o texto sai do `code` da R4.3 e, sem código conhecido, do status.
 */
const MENSAGEM_POR_CODIGO: Record<string, string> = {
  CHAVES_ACESSO_NEGADO: 'Você não tem permissão para administrar as chaves deste escritório.',
  CHAVES_TETO_VIGENTE: 'O escritório já tem uma chave vigente. Para trocá-la, use rotacionar.',
  CHAVES_NAO_ENCONTRADA: 'A chave não foi encontrada. A lista foi atualizada.',
  CHAVES_NAO_VIGENTE: 'Só a chave vigente pode ser rotacionada. A lista foi atualizada.',
};
const MENSAGEM_POR_STATUS: Record<number, string> = {
  400: 'O pedido não foi aceito. Confira os dados da chave.',
  403: 'Você não tem permissão para administrar as chaves deste escritório.',
  404: 'A chave não foi encontrada. A lista foi atualizada.',
  409: 'O escritório já tem uma chave vigente. Para trocá-la, use rotacionar.',
  422: 'Só a chave vigente pode ser rotacionada. A lista foi atualizada.',
};
const MENSAGEM_GENERICA = 'Não foi possível concluir a operação. Tente novamente.';

/**
 * Rejeição tipada: o thunk a passa adiante como `ErroDasChaves` e a tela decide pelo `codigo`
 * (`CHAVES_TETO_VIGENTE` etc.) sem comparar texto.
 */
export class FalhaDasChaves extends Error {
  readonly erro: ErroDasChaves;

  constructor(erro: ErroDasChaves) {
    super(erro.mensagem);
    this.name = 'FalhaDasChaves';
    this.erro = erro;
  }
}

interface CorpoDaApi<T> {
  success?: boolean;
  data?: T;
  error?: { code?: unknown };
}

async function lerResposta<T>(response: Response): Promise<T> {
  const json = (await response.json().catch(() => null)) as CorpoDaApi<T> | null;
  if (!response.ok) {
    const codigo = typeof json?.error?.code === 'string' ? json.error.code : null;
    const mensagem =
      (codigo ? MENSAGEM_POR_CODIGO[codigo] : undefined) ??
      MENSAGEM_POR_STATUS[response.status] ??
      MENSAGEM_GENERICA;
    throw new FalhaDasChaves({ status: response.status, codigo, mensagem });
  }
  if (json == null || json.data === undefined) {
    throw new FalhaDasChaves({
      status: response.status,
      codigo: null,
      mensagem: MENSAGEM_GENERICA,
    });
  }
  return json.data;
}

/** Separa o texto do DTO já aqui: o resto da tela nunca vê os dois no mesmo objeto. */
function projetarChaveEmitida(dados: ChaveEmitidaDaApi): ChaveEmitida {
  const { textoDaChave: texto, ...chave } = dados;
  if (typeof texto !== 'string' || texto.length === 0) {
    throw new FalhaDasChaves({ status: 200, codigo: null, mensagem: MENSAGEM_GENERICA });
  }
  return { chave, texto };
}

export const chavesDeApiService = {
  async listar(escritorioId: string): Promise<ChaveDoCliente[]> {
    const response = await chamarRotaDasChaves(rotaDasChaves(escritorioId), 'GET');
    return lerResposta<ChaveDoCliente[]>(response);
  },

  async criar(escritorioId: string, dados: DadosDaChaveNova): Promise<ChaveEmitida> {
    const response = await chamarRotaDasChaves(rotaDasChaves(escritorioId), 'POST', {
      nome: dados.nome.trim(),
      escopos: dados.escopos,
    });
    return projetarChaveEmitida(await lerResposta<ChaveEmitidaDaApi>(response));
  },

  async rotacionar(escritorioId: string, rotacao: RotacaoDeChave): Promise<ChaveEmitida> {
    const response = await chamarRotaDasChaves(
      `${rotaDaChave(escritorioId, rotacao.chaveId)}/rotacionar`,
      'POST',
      { nome: rotacao.nome.trim() },
    );
    return projetarChaveEmitida(await lerResposta<ChaveEmitidaDaApi>(response));
  },

  /** Revogar de novo é 200 no core (idempotente): a tela não precisa distinguir. */
  async revogar(escritorioId: string, chaveId: string): Promise<void> {
    const response = await chamarRotaDasChaves(
      `${rotaDaChave(escritorioId, chaveId)}/revogar`,
      'POST',
    );
    await lerResposta<unknown>(response);
  },
};
